<script setup lang="ts">
/**
 * Create/Edit form for a treasury bank account. Used by
 * TreasuryAccountFormDrawer in both `create` and `edit` modes.
 *
 * Validation lives here (`isFormValid`). The container handles the
 * mutation + API error display.
 *
 * Field rules per the admin-service contract:
 *   - accountNumber / currencyCode / countryCode are immutable on edit
 *     (admin-service does not accept changes via PATCH).
 *   - isDefault is only settable on create. To change default
 *     post-creation, use the "Set as default" row action.
 */
import { computed, reactive, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Input from '@uiKit/elements/Input.vue'
import Textarea from '@uiKit/elements/Textarea.vue'
import ToggleSwitch from '@uiKit/elements/ToggleSwitch.vue'
import Message from '@uiKit/elements/Message.vue'
import FormField from '@uiKit/components/FormField.vue'

export type TreasuryAccountFormMode = 'create' | 'edit'

export interface TreasuryAccountFormValues {
  accountName: string
  bankName: string
  accountNumber: string
  iban: string
  swiftBic: string
  currencyCode: string
  countryCode: string
  notes: string
  isDefault: boolean
}

interface Props {
  mode: TreasuryAccountFormMode
  initialValues?: Partial<TreasuryAccountFormValues>
  loading: boolean
  apiError: string | null
}

const props = withDefaults(defineProps<Props>(), {
  initialValues: () => ({}),
  apiError: null,
})

const emit = defineEmits<{
  (e: 'submit', values: TreasuryAccountFormValues): void
  (e: 'cancel'): void
}>()

const draft = reactive<TreasuryAccountFormValues>({
  accountName: '',
  bankName: '',
  accountNumber: '',
  iban: '',
  swiftBic: '',
  currencyCode: 'AED',
  countryCode: 'AE',
  notes: '',
  isDefault: false,
})

function hydrateFromProps() {
  draft.accountName = props.initialValues.accountName ?? ''
  draft.bankName = props.initialValues.bankName ?? ''
  draft.accountNumber = props.initialValues.accountNumber ?? ''
  draft.iban = props.initialValues.iban ?? ''
  draft.swiftBic = props.initialValues.swiftBic ?? ''
  draft.currencyCode = props.initialValues.currencyCode ?? 'AED'
  draft.countryCode = props.initialValues.countryCode ?? 'AE'
  draft.notes = props.initialValues.notes ?? ''
  draft.isDefault = props.initialValues.isDefault ?? false
}

watch(
  () => props.initialValues,
  () => {
    hydrateFromProps()
  },
  { immediate: true, deep: true },
)

// Reset the "edited since last server error" gate on every new server
// error so the latest error renders inline. Operators retyping in the
// affected field clears the error via handleFieldUpdate.
const hasEditedSinceApiError = ref(false)
watch(
  () => props.apiError,
  () => {
    hasEditedSinceApiError.value = false
  },
)
function markEdited() {
  hasEditedSinceApiError.value = true
}

const visibleApiError = computed(() =>
  props.apiError && !hasEditedSinceApiError.value ? props.apiError : '',
)

const isCreateMode = computed(() => props.mode === 'create')

const isFormValid = computed(() => {
  if (!draft.accountName.trim()) return false
  if (!draft.bankName.trim()) return false
  if (isCreateMode.value && !draft.accountNumber.trim()) return false
  if (!draft.currencyCode.trim()) return false
  if (!draft.countryCode.trim()) return false
  return true
})

function handleSubmit() {
  if (!isFormValid.value) return
  emit('submit', {
    accountName: draft.accountName.trim(),
    bankName: draft.bankName.trim(),
    accountNumber: draft.accountNumber.trim(),
    iban: draft.iban.trim(),
    swiftBic: draft.swiftBic.trim(),
    currencyCode: draft.currencyCode.trim(),
    countryCode: draft.countryCode.trim(),
    notes: draft.notes.trim(),
    isDefault: draft.isDefault,
  })
}
</script>

<template>
  <Box flex direction="col" class="form-body">
    <Box flex direction="col" gap="4" class="form-body__content">
      <FormField label="Account name" required>
        <Input
          v-model="draft.accountName"
          placeholder="Sliceal Treasury — AED"
          @update:model-value="markEdited"
        />
      </FormField>

      <FormField label="Bank name" required>
        <Input
          v-model="draft.bankName"
          placeholder="Emirates NBD"
          @update:model-value="markEdited"
        />
      </FormField>

      <FormField
        label="Account number"
        :required="isCreateMode"
        :helper-text="
          isCreateMode ? undefined : 'Account number is immutable after the account is created.'
        "
      >
        <Input
          v-model="draft.accountNumber"
          placeholder="0123456789012345"
          :disabled="!isCreateMode"
        />
      </FormField>

      <FormField label="IBAN">
        <Input
          v-model="draft.iban"
          placeholder="AE070331234567890123456"
          @update:model-value="markEdited"
        />
      </FormField>

      <FormField label="SWIFT / BIC">
        <Input v-model="draft.swiftBic" placeholder="EBILAEAD" @update:model-value="markEdited" />
      </FormField>

      <Box flex gap="3">
        <FormField label="Currency" required class="form-body__currency">
          <Input v-model="draft.currencyCode" placeholder="AED" :disabled="!isCreateMode" />
        </FormField>
        <FormField label="Country" required class="form-body__country">
          <Input v-model="draft.countryCode" placeholder="AE" :disabled="!isCreateMode" />
        </FormField>
      </Box>

      <FormField label="Notes">
        <Textarea
          v-model="draft.notes"
          placeholder="Primary treasury account for AED settlements."
          :rows="3"
          @update:model-value="markEdited"
        />
      </FormField>

      <Box v-if="isCreateMode" flex align="center" justify="between" gap="3">
        <Box flex direction="col" gap="1">
          <Text type="body" weight="medium">Set as default for this currency</Text>
          <Text type="caption" color="muted">
            Clears the existing default and points new settlements at this account.
          </Text>
        </Box>
        <ToggleSwitch v-model="draft.isDefault" />
      </Box>

      <Message v-if="visibleApiError" color="danger" variant="outlined" size="small">
        <Text type="body-sm">{{ visibleApiError }}</Text>
      </Message>
    </Box>

    <Box class="form-body__actions">
      <Button
        :label="isCreateMode ? 'Create account' : 'Save changes'"
        color="primary"
        block
        size="large"
        :loading="loading"
        :disabled="!isFormValid || loading"
        @click="handleSubmit"
      />
    </Box>
  </Box>
</template>

<style scoped>
.form-body {
  min-height: 100%;
  padding: 1.25rem 1.5rem;
}

.form-body__content {
  flex: 1;
}

.form-body__currency,
.form-body__country {
  flex: 1;
}

.form-body__actions {
  margin-top: auto;
  padding-top: 1rem;
}

@media (max-width: 992px) {
  .form-body {
    padding: 1rem;
  }
}
</style>
